import type { Input, Edge } from "@/workflow_api/types/workflow";
import type { WaitNodeFormData } from "@/schemas/workflow/wait-node";

export const defaultWaitConfig: WaitNodeFormData = {
  mode: "wait_all",
  timeoutValue: 30,
  timeoutUnit: "minute",
};

/**
 * Representa una conexión de entrada al nodo Wait
 */
export interface WaitInput {
  id?: string; // UUID del input (viene del backend o se genera)
  name: string; // Nombre visible del input (ej: "Entrada 1")
  sourceConnections: WaitSourceConnection[]; // Conexiones desde otros nodos
}

/**
 * Representa una conexión desde un nodo fuente hacia una entrada del Wait
 */
export interface WaitSourceConnection {
  sourceNodeId: string; // ID del nodo fuente
  sourceHandle: string; // Handle de salida del nodo fuente (ej: "thread_1")
}

/**
 * Edge que se envía al backend
 */
export interface WaitEdge {
  source: string;
  source_handler: string;
  target_handler: string;
  target_input_name: string;
}

/**
 * Serializa los inputs del Wait para enviar al backend
 */
export const serializeWaitInputs = (
  waitInputs: WaitInput[],
  multiThreadId: string,
): Input => {
  return [
    ...waitInputs.map((input, index) => ({
      id: input.id,
      name: input.name || `Entrada ${index + 1}`,
      type: "THREAD" as const,
      value: null,
      // El backend espera source_connections en este formato
      source_connections: input.sourceConnections.map((conn) => ({
        source_node_id: conn.sourceNodeId,
        source_node_output_id: conn.sourceHandle,
        field: {
          id: conn.sourceHandle,
          name: conn.sourceHandle,
          type: "thread",
        },
      })),
    })),
    // multihilos id value
    {
      name: "multithread_id",
      type: "STRING",
      value: multiThreadId,
    },
  ];
};

/**
 * Genera el array de edges para enviar al backend
 */
export const serializeWaitEdges = (waitInputs: WaitInput[]): WaitEdge[] => {
  const edges: WaitEdge[] = [];

  waitInputs.forEach((input, inputIndex) => {
    input.sourceConnections.forEach((conn) => {
      edges.push({
        source: conn.sourceNodeId,
        source_handler: conn.sourceHandle,
        target_handler: `input-${inputIndex + 1}`,
        target_input_name: input.name || `Entrada ${inputIndex + 1}`,
      });
    });
  });

  return edges;
};

/**
 * Deserializa los inputs del backend al formato interno.
 * Usa los edges para obtener el source_handler correcto ya que
 * source_connections.field puede venir null desde el backend.
 */
export const deserializeWaitInputs = (
  inputs?: Input,
  edges?: Edge[],
): WaitInput[] => {
  if (!inputs || inputs.length === 0) {
    return [];
  }

  // Crear un mapa de edges por target_handler para lookup rápido
  // El edge tiene: source, source_handler, target_handler (ej: "input-1")
  const edgesByTargetHandler = new Map<string, Edge>();
  if (edges && edges.length > 0) {
    edges.forEach((edge) => {
      if (edge.target_handler?.startsWith("input-")) {
        edgesByTargetHandler.set(edge.target_handler, edge);
      }
    });
  }

  return inputs
    .filter((input) => input.type === "THREAD")
    .map((input, index) => {
      const sourceConnections: WaitSourceConnection[] = [];
      const targetHandler = `input-${index + 1}`;

      // Buscar el edge correspondiente a este input
      const matchingEdge = edgesByTargetHandler.get(targetHandler);

      // Extraer source_connections del input
      if (Array.isArray(input.source_connections)) {
        input.source_connections.forEach((conn) => {
          if (conn?.source_node_id) {
            // Prioridad para obtener el source_handler:
            // 1. field.id del source_connection (si existe)
            // 2. source_node_output_id del source_connection
            // 3. source_handler del edge correspondiente
            // 4. Fallback a "source" (esto no debería pasar)
            let sourceHandle = conn.field?.id || conn.source_node_output_id;

            if (
              !sourceHandle &&
              matchingEdge &&
              matchingEdge.source === conn.source_node_id
            ) {
              sourceHandle = matchingEdge.source_handler;
            }

            sourceConnections.push({
              sourceNodeId: conn.source_node_id,
              sourceHandle: sourceHandle || "source",
            });
          }
        });
      }

      return {
        id: input.id,
        name: input.name || `Entrada ${index + 1}`,
        sourceConnections,
      };
    });
};

/**
 * Deserializa la configuración del Wait (modo, timeout)
 * Por ahora devuelve valores por defecto
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const deserializeWaitConfig = (_inputs?: Input): WaitNodeFormData => {
  // La configuración base ya no se guarda en inputs
  return defaultWaitConfig;
};
